import axiosInstance from '../api/axiosInstance';
import { PaginatedResponse } from '@/app/types/api';
import { VoucherResponse } from '@/app/types/voucher'; // Assuming this contains needed invoice fields
import {
  PaymentReceivedMultiInvoiceCreate,
  PaymentMadeMultiInvoiceCreate,
  UnallocatedAmountResponse,
  VoucherWithCreditResponse,
  AllocateVoucherCreditRequest,
  CreditNoteRefundCreate,
  OutstandingCreditNote
} from '@/app/types/payment';

/**
 * Records a payment received from a party, allocated across multiple invoices.
 * @param data - The payment and allocation details.
 * @returns The created Payment Received voucher details.
 */
export const recordPaymentIn = async (data: PaymentReceivedMultiInvoiceCreate): Promise<VoucherResponse> => {
  try {
    const response = await axiosInstance.post<VoucherResponse>('/payments/receive-multi', data);
    return response.data;
  } catch (error) {
    console.error('Error recording payment:', error);
    throw error; // Re-throw to be caught by react-query mutation
  }
};

/**
 * Fetches outstanding (unpaid or partially paid) sales invoices for a specific party.
 * @param partyId - The ID of the party.
 * @returns A list of outstanding sales invoice vouchers.
 */
export const listOutstandingInvoices = async (partyId: number): Promise<VoucherResponse[]> => {
  try {
    // Note: Backend returns a simple list here, not paginated
    const response = await axiosInstance.get<VoucherResponse[]>(`/payments/parties/${partyId}/outstanding-invoices`);
    // Ensure amount_due is a number, default to total_amount if null/undefined
    return response.data.map(invoice => ({
        ...invoice,
        amount_due: invoice.amount_due === null || invoice.amount_due === undefined
                      ? invoice.total_amount // Assume full amount due if null
                      : Number(invoice.amount_due) // Ensure it's a number
    }));
  } catch (error) {
    console.error(`Error fetching outstanding invoices for party ${partyId}:`, error);
    throw error; // Re-throw to be caught by react-query query
  }
};

/**
 * Records a payment made to a party, allocated across multiple purchase invoices.
 * @param data - The payment and allocation details.
 * @returns The created Payment Made voucher details.
 */
export const recordPaymentOut = async (data: PaymentMadeMultiInvoiceCreate): Promise<VoucherResponse> => {
  try {
    const response = await axiosInstance.post<VoucherResponse>('/payments/make-multi', data);
    return response.data;
  } catch (error) {
    console.error('Error recording payment out:', error);
    throw error; // Re-throw to be caught by react-query mutation
  }
};

/**
 * Fetches outstanding (unpaid or partially paid) purchase invoices for a specific party.
 * @param partyId - The ID of the party.
 * @returns A list of outstanding purchase invoice vouchers.
 */
export const listOutstandingPurchaseInvoices = async (partyId: number): Promise<VoucherResponse[]> => {
  try {
    // Note: Backend returns a simple list here, not paginated
    const response = await axiosInstance.get<VoucherResponse[]>(`/payments/parties/${partyId}/outstanding-purchase-invoices`);
    // Ensure amount_due is a number, default to total_amount if null/undefined
    return response.data.map(invoice => ({
        ...invoice,
        amount_due: invoice.amount_due === null || invoice.amount_due === undefined
                      ? invoice.total_amount // Assume full amount due if null
                      : Number(invoice.amount_due) // Ensure it's a number
    }));
  } catch (error) {
    console.error(`Error fetching outstanding purchase invoices for party ${partyId}:`, error);
    throw error; // Re-throw to be caught by react-query query
  }
};

/**
 * Records an on-account payment from a party without allocating to specific invoices.
 * The payment is tracked as unallocated credit and can be applied to future invoices.
 * @param data - The on-account payment details.
 * @returns The created voucher details.
 */
export interface OnAccountPaymentCreate {
  party_id: number;
  received_in_account_id: number;
  payment_amount: string;
  transaction_date: string;
  reference_number?: string;
  narration?: string;
  site_id?: number;
}

export const receiveOnAccountPayment = async (data: OnAccountPaymentCreate): Promise<VoucherResponse> => {
  try {
    const response = await axiosInstance.post<VoucherResponse>('/payments/receive-on-account', data);
    return response.data;
  } catch (error) {
    console.error('Error recording on-account payment:', error);
    throw error; // Re-throw to be caught by react-query mutation
  }
};

/**
 * Get the unallocated amount for a specific payment voucher.
 * @param paymentVoucherId - The ID of the payment voucher.
 * @returns The unallocated amount details.
 */
export const getUnallocatedAmount = async (paymentVoucherId: number): Promise<UnallocatedAmountResponse> => {
  try {
    const response = await axiosInstance.get<UnallocatedAmountResponse>(`/payments/${paymentVoucherId}/unallocated`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching unallocated amount for payment ${paymentVoucherId}:`, error);
    throw error;
  }
};

/**
 * Get all payment vouchers for a party that have unallocated amounts.
 * @param partyId - The ID of the party.
 * @returns List of payment vouchers with unallocated credit.
 */
export const getVouchersWithCredit = async (partyId: number): Promise<VoucherWithCreditResponse[]> => {
  try {
    const response = await axiosInstance.get<VoucherWithCreditResponse[]>(`/payments/parties/${partyId}/vouchers-with-credit`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching vouchers with credit for party ${partyId}:`, error);
    throw error;
  }
};

/**
 * Allocate unallocated credit from a payment voucher to invoices.
 * @param data - The allocation request details.
 * @returns The updated payment voucher.
 */
export const allocateVoucherCredit = async (data: AllocateVoucherCreditRequest): Promise<VoucherResponse> => {
  try {
    const response = await axiosInstance.post<VoucherResponse>('/payments/allocate-voucher-credit', data);
    return response.data;
  } catch (error) {
    console.error('Error allocating voucher credit:', error);
    throw error;
  }
};

/**
 * Fetches outstanding credit notes for a specific party that can be refunded.
 * @param partyId - The ID of the party.
 * @returns A list of outstanding credit notes.
 */
export const listOutstandingCreditNotes = async (partyId: number): Promise<OutstandingCreditNote[]> => {
  try {
    const response = await axiosInstance.get<OutstandingCreditNote[]>(
      `/payments/parties/${partyId}/outstanding-credit-notes`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching outstanding credit notes for party ${partyId}:`, error);
    throw error;
  }
};

/**
 * Creates a credit note refund (Payment Made) for a customer.
 * @param data - The refund details including allocations.
 * @returns The created Payment Made voucher details.
 */
export const refundCreditNote = async (data: CreditNoteRefundCreate): Promise<VoucherResponse> => {
  try {
    const response = await axiosInstance.post<VoucherResponse>('/payments/refund-credit-note', data);
    return response.data;
  } catch (error) {
    console.error('Error creating credit note refund:', error);
    throw error;
  }
}; 